/**
 * The routes that render to anyone, with no vendor session (TRO-1188).
 *
 * Every one is a sibling of `(app)` in the router tree, so none mounts the `ClerkProvider` that
 * `(app)/_layout.tsx` provides. That is only half the story: Expo Router holds
 * `anchor: "(app)"` at the bottom of the stack, so the group stays mounted *beneath* a
 * public deep link unless the layout declines to render it. This is what it declines on.
 *
 * `browse` and `shows` were here from the start (#387). `shop`, `shops` and `inventory` are the
 * storefront paths from TRO-1153 and TRO-1187, and were missed when those shipped — which is why
 * `app.trovie.io/shop/<slug>` painted the vendor sign-in screen before the storefront.
 */

/**
 * Public only with a slug under them, because the bare path is a vendor screen:
 * `/shop` is Shop account and `/inventory` is the Inventory tab. Treating those as public
 * unmounts the whole vendor app underneath a screen a signed-in vendor is looking at — which is
 * a blank screen, not a sign-in one, and is what `j5-desktop-inventory.auth.spec.ts` caught.
 *
 * `/shops` has no vendor screen today, but its public form always carries a slug too, and a rule
 * that is the same for all three is one fewer thing to get wrong when the next one is added.
 */
const PUBLIC_WITH_SLUG = ["shop", "shops", "inventory"] as const;

/** Public at the bare path as well as below it. None has a vendor route of the same name. `out`
 *  holds the pages that forward a click off-site in the browser (`/out/ebay-solds`); `t` is the
 *  Table display board a Vendor puts on a monitor (`/t`, `/t/<code>`). */
const PUBLIC_AT_ANY_DEPTH = ["browse", "shows", "prototype-public-show", "out", "t"] as const;

/**
 * Whether `pathname` renders to an anonymous visitor.
 *
 * Matches whole segments, never a string prefix: `/shopping` and `/shops-report` both start with
 * `/shop`, and handing either to anonymous rendering would drop a vendor screen out of its
 * providers.
 *
 * `/shows/invitations/<token>` is deliberately excluded. It shares the `/shows` prefix but is an
 * emailed invitation that signs a vendor in, so it belongs to the authenticated group.
 */
export function isPublicRoutePath(pathname: string): boolean {
  // A caller may hand this a full location path. `usePathname()` strips both, but the cost of
  // being wrong here is a sign-in screen over a scanned card, so do not depend on the caller.
  const path = pathname.split(/[?#]/, 1)[0] ?? "";
  const [, first, second] = path.split("/");
  if (first === "shows" && second === "invitations") return false;
  if ((PUBLIC_AT_ANY_DEPTH as readonly string[]).includes(first ?? "")) return true;
  return (PUBLIC_WITH_SLUG as readonly string[]).includes(first ?? "") && Boolean(second);
}
